/**
 * TimeClockPage - Système de pointage professionnel
 * Pointage multi-facteurs, tracking activité, compte-rendu journalier
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Clock, MapPin, Camera, Wifi, Fingerprint, LogIn, LogOut,
  Coffee, AlertCircle, CheckCircle, FileText, Calendar,
  Timer, Activity, TrendingUp, User, ChevronRight, X,
  Play, Pause, RefreshCw, Send, Smile, Meh, Frown
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

import { API_URL } from '../../config/api';
const API = `${API_URL}/api`;

// ══════════════════════════════════════════════════════════════════════════════
// HOOKS
// ══════════════════════════════════════════════════════════════════════════════

// Hook pour la géolocalisation
const useGeolocation = () => {
  const [location, setLocation] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const getLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setError("Géolocalisation non supportée");
      return;
    }

    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocation({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          timestamp: new Date().toISOString(),
        });
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  return { location, error, loading, getLocation };
};

// Hook pour le device fingerprint
const useDeviceFingerprint = () => {
  const [fingerprint, setFingerprint] = useState(null);

  useEffect(() => {
    // Créer un fingerprint basique
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('fingerprint', 2, 2);
    
    const data = [
      navigator.userAgent,
      navigator.language,
      screen.width + 'x' + screen.height,
      new Date().getTimezoneOffset(),
      canvas.toDataURL(),
    ].join('|');
    
    // Hash simple
    let hash = 0;
    for (let i = 0; i < data.length; i++) {
      const char = data.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    
    setFingerprint(Math.abs(hash).toString(36));
  }, []);

  return fingerprint;
};

// Hook pour le tracking d'activité
const useActivityTracker = (isActive, userId, deviceFingerprint) => {
  const lastInputRef = useRef(Date.now());
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!isActive || !userId || !deviceFingerprint) return;

    // Tracker les événements clavier/souris
    const updateLastInput = () => {
      lastInputRef.current = Date.now();
    };

    window.addEventListener('keydown', updateLastInput);
    window.addEventListener('mousemove', updateLastInput);
    window.addEventListener('click', updateLastInput);
    window.addEventListener('scroll', updateLastInput);

    // Envoyer un ping toutes les 2 minutes
    intervalRef.current = setInterval(async () => {
      const secondsAgo = Math.floor((Date.now() - lastInputRef.current) / 1000);
      
      try {
        await axios.post(`${API}/timeclock/activity/ping`, {
          user_id: userId,
          device_fingerprint: deviceFingerprint,
          activity_type: secondsAgo > 600 ? 'idle' : 'active',
          last_input_seconds_ago: secondsAgo,
        });
      } catch (err) {
        console.error('Activity ping failed:', err);
      }
    }, 120000); // 2 minutes

    return () => {
      window.removeEventListener('keydown', updateLastInput);
      window.removeEventListener('mousemove', updateLastInput);
      window.removeEventListener('click', updateLastInput);
      window.removeEventListener('scroll', updateLastInput);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isActive, userId, deviceFingerprint]);
};

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANTS
// ══════════════════════════════════════════════════════════════════════════════

// Modal Selfie
const SelfieModal = ({ onCapture, onClose }) => {
  const videoRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [captured, setCaptured] = useState(null);

  useEffect(() => {
    const startCamera = async () => {
      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({ 
          video: { facingMode: 'user', width: 480, height: 480 } 
        });
        setStream(mediaStream);
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
      } catch (err) {
        toast.error("Impossible d'accéder à la caméra");
        onClose();
      }
    };
    startCamera();

    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const capture = () => {
    const canvas = document.createElement('canvas');
    canvas.width = 480;
    canvas.height = 480;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(videoRef.current, 0, 0, 480, 480);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
    setCaptured(dataUrl);
  };

  const confirm = () => {
    onCapture(captured);
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
      >
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="font-semibold flex items-center gap-2">
            <Camera className="text-orange-500" size={20} />
            Photo de pointage
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X size={20} />
          </button>
        </div>

        <div className="p-4">
          {!captured ? (
            <>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full aspect-square object-cover rounded-xl bg-gray-900"
              />
              <Button onClick={capture} className="w-full mt-4 bg-orange-500 hover:bg-orange-600">
                <Camera className="mr-2" size={18} /> Prendre la photo
              </Button>
            </>
          ) : (
            <>
              <img src={captured} alt="Selfie" className="w-full aspect-square object-cover rounded-xl" />
              <div className="flex gap-2 mt-4">
                <Button variant="outline" onClick={() => setCaptured(null)} className="flex-1">
                  Reprendre
                </Button>
                <Button onClick={confirm} className="flex-1 bg-green-500 hover:bg-green-600">
                  <CheckCircle className="mr-2" size={18} /> Valider
                </Button>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

// Modal Compte-Rendu
const DailyReportModal = ({ userId, date, onSubmit, onClose }) => {
  const [tasks, setTasks] = useState([{ description: '', duration_minutes: 30, status: 'completed' }]);
  const [summary, setSummary] = useState('');
  const [challenges, setChallenges] = useState('');
  const [tomorrowPlan, setTomorrowPlan] = useState('');
  const [mood, setMood] = useState(3);
  const [saving, setSaving] = useState(false);

  const addTask = () => {
    setTasks([...tasks, { description: '', duration_minutes: 30, status: 'completed' }]);
  };

  const updateTask = (index, field, value) => {
    const newTasks = [...tasks];
    newTasks[index][field] = value;
    setTasks(newTasks);
  };

  const removeTask = (index) => {
    setTasks(tasks.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!summary.trim()) {
      toast.error('Le résumé est obligatoire');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/timeclock/report/submit`, {
        user_id: userId,
        date,
        tasks: tasks.filter(t => t.description.trim()),
        summary,
        challenges: challenges || null,
        tomorrow_plan: tomorrowPlan || null,
        mood,
      });
      toast.success('Compte-rendu soumis');
      onSubmit();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-xl w-full max-w-2xl my-8"
      >
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <FileText className="text-orange-500" />
            Compte-rendu du {new Date(date).toLocaleDateString('fr-FR')}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Tâches */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tâches accomplies aujourd'hui
            </label>
            <div className="space-y-2">
              {tasks.map((task, i) => (
                <div key={i} className="flex gap-2">
                  <input
                    type="text"
                    value={task.description}
                    onChange={(e) => updateTask(i, 'description', e.target.value)}
                    placeholder="Description de la tâche..."
                    className="flex-1 px-3 py-2 border rounded-lg text-sm"
                  />
                  <input
                    type="number"
                    value={task.duration_minutes}
                    onChange={(e) => updateTask(i, 'duration_minutes', parseInt(e.target.value) || 0)}
                    className="w-20 px-2 py-2 border rounded-lg text-sm text-center"
                    placeholder="min"
                  />
                  <select
                    value={task.status}
                    onChange={(e) => updateTask(i, 'status', e.target.value)}
                    className="w-32 px-2 py-2 border rounded-lg text-sm"
                  >
                    <option value="completed">Terminé</option>
                    <option value="in_progress">En cours</option>
                    <option value="blocked">Bloqué</option>
                  </select>
                  {tasks.length > 1 && (
                    <button onClick={() => removeTask(i)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg">
                      <X size={18} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={addTask} className="mt-2">
              + Ajouter une tâche
            </Button>
          </div>

          {/* Résumé */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Résumé de la journée *
            </label>
            <textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="Qu'as-tu accompli aujourd'hui ?"
            />
          </div>

          {/* Difficultés */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Difficultés rencontrées (optionnel)
            </label>
            <textarea
              value={challenges}
              onChange={(e) => setChallenges(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="Y a-t-il eu des blocages ou problèmes ?"
            />
          </div>

          {/* Plan demain */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Plan pour demain (optionnel)
            </label>
            <textarea
              value={tomorrowPlan}
              onChange={(e) => setTomorrowPlan(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="Que prévois-tu de faire demain ?"
            />
          </div>

          {/* Mood */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Comment te sens-tu ?
            </label>
            <div className="flex gap-2 justify-center">
              {[1, 2, 3, 4, 5].map((val) => (
                <button
                  key={val}
                  onClick={() => setMood(val)}
                  className={`p-3 rounded-xl transition-all ${
                    mood === val 
                      ? 'bg-orange-500 text-white scale-110' 
                      : 'bg-gray-100 hover:bg-gray-200'
                  }`}
                >
                  {val <= 2 ? <Frown size={24} /> : val >= 4 ? <Smile size={24} /> : <Meh size={24} />}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-end gap-3 rounded-b-2xl">
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={handleSubmit} disabled={saving} className="bg-orange-500 hover:bg-orange-600">
            {saving ? 'Envoi...' : 'Soumettre le compte-rendu'}
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

// Modal Absence
const AbsenceModal = ({ userId, onStart, onClose }) => {
  const [reason, setReason] = useState('break');
  const [duration, setDuration] = useState(15);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const reasons = [
    { id: 'break', label: 'Pause', icon: Coffee },
    { id: 'meeting', label: 'Réunion', icon: User },
    { id: 'personal', label: 'Personnel', icon: User },
    { id: 'other', label: 'Autre', icon: AlertCircle },
  ];

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/timeclock/absence/start`, {
        user_id: userId,
        reason,
        expected_duration_minutes: duration,
        notes: notes || null,
      });
      toast.success('Absence enregistrée');
      onStart();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-xl w-full max-w-md"
      >
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="font-semibold">Déclarer une absence</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X size={20} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Raison</label>
            <div className="grid grid-cols-2 gap-2">
              {reasons.map((r) => (
                <button
                  key={r.id}
                  onClick={() => setReason(r.id)}
                  className={`p-3 rounded-xl flex items-center gap-2 transition-all ${
                    reason === r.id
                      ? 'bg-orange-100 text-orange-700 border-2 border-orange-500'
                      : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                  }`}
                >
                  <r.icon size={18} />
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Durée prévue (minutes)
            </label>
            <input
              type="number"
              value={duration}
              onChange={(e) => setDuration(parseInt(e.target.value) || 0)}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optionnel)</label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="Précisions..."
            />
          </div>
        </div>

        <div className="p-4 border-t flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={handleSubmit} disabled={saving} className="bg-yellow-500 hover:bg-yellow-600">
            {saving ? 'Enregistrement...' : "M'absenter"}
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// PAGE PRINCIPALE
// ══════════════════════════════════════════════════════════════════════════════

export default function TimeClockPage() {
  // State
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clockingIn, setClockingIn] = useState(false);
  const [clockingOut, setClockingOut] = useState(false);
  const [showSelfie, setShowSelfie] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [showAbsence, setShowAbsence] = useState(false);
  const [selfieData, setSelfieData] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [pendingAction, setPendingAction] = useState(null); // 'clock-in' or 'clock-out'

  // Hooks
  const { location, loading: geoLoading, getLocation } = useGeolocation();
  const deviceFingerprint = useDeviceFingerprint();

  // TODO: Get real user ID from auth context
  const userId = 'current-user-id';
  const today = new Date().toISOString().split('T')[0];

  // Activity tracker
  useActivityTracker(status?.is_clocked_in, userId, deviceFingerprint);

  // Fetch status
  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/timeclock/status/${userId}`);
      setStatus(res.data);
    } catch (error) {
      console.error('Error fetching status:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    getLocation();
  }, []);

  // Timer
  useEffect(() => {
    if (!status?.is_clocked_in || !status?.entry?.clock_in) return;

    const clockIn = new Date(status.entry.clock_in);
    const updateElapsed = () => {
      const now = new Date();
      const diff = Math.floor((now - clockIn) / 1000);
      setElapsedTime(diff);
    };

    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);
    return () => clearInterval(interval);
  }, [status?.is_clocked_in, status?.entry?.clock_in]);

  // Format time
  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  // Handle clock in
  const handleClockIn = async () => {
    setPendingAction('clock-in');
    setShowSelfie(true);
  };

  const performClockIn = async (selfieUrl) => {
    setClockingIn(true);
    try {
      await axios.post(`${API}/timeclock/clock-in`, {
        user_id: userId,
        geolocation: location,
        selfie_url: selfieUrl,
        wifi_ssid: null, // Could be implemented with native app
        device_fingerprint: deviceFingerprint,
        clock_type: 'regular',
      });
      toast.success('Pointage enregistré');
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de pointage');
    } finally {
      setClockingIn(false);
      setSelfieData(null);
    }
  };

  // Handle clock out
  const handleClockOut = async () => {
    if (!status?.has_daily_report) {
      toast.error('Tu dois d\'abord soumettre ton compte-rendu journalier');
      setShowReport(true);
      return;
    }
    setPendingAction('clock-out');
    setShowSelfie(true);
  };

  const performClockOut = async (selfieUrl) => {
    setClockingOut(true);
    try {
      const res = await axios.post(`${API}/timeclock/clock-out`, {
        user_id: userId,
        geolocation: location,
        selfie_url: selfieUrl,
        device_fingerprint: deviceFingerprint,
      });
      toast.success(`Journée terminée: ${res.data.total_hours}h travaillées`);
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de pointage');
    } finally {
      setClockingOut(false);
      setSelfieData(null);
    }
  };

  // Handle selfie capture
  const handleSelfieCapture = (dataUrl) => {
    if (pendingAction === 'clock-in') {
      performClockIn(dataUrl);
    } else if (pendingAction === 'clock-out') {
      performClockOut(dataUrl);
    }
    setPendingAction(null);
  };

  // Handle absence
  const handleEndAbsence = async () => {
    try {
      await axios.post(`${API}/timeclock/absence/end`, { user_id: userId });
      toast.success('Retour enregistré');
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500" />
      </div>
    );
  }

  const isClockedIn = status?.is_clocked_in;
  const isClockedOut = status?.is_clocked_out;
  const currentAbsence = status?.current_absence;

  return (
    <div className="max-w-2xl mx-auto p-4 space-y-6" data-testid="timeclock-page">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center justify-center gap-3">
          <Clock className="text-orange-500" />
          Pointage
        </h1>
        <p className="text-gray-500 mt-1">
          {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        </p>
      </div>

      {/* Timer Card */}
      <Card className={`border-2 ${isClockedIn ? 'border-green-500 bg-green-50' : isClockedOut ? 'border-gray-300 bg-gray-50' : 'border-orange-500 bg-orange-50'}`}>
        <CardContent className="p-8 text-center">
          {isClockedIn && !currentAbsence && (
            <>
              <div className="flex items-center justify-center gap-2 text-green-600 mb-2">
                <Activity className="animate-pulse" />
                <span className="font-medium">En cours</span>
              </div>
              <div className="text-6xl font-mono font-bold text-gray-900 mb-4">
                {formatTime(elapsedTime)}
              </div>
              <p className="text-sm text-gray-500">
                Pointé à {new Date(status.entry.clock_in).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
              </p>
            </>
          )}

          {currentAbsence && (
            <>
              <div className="flex items-center justify-center gap-2 text-yellow-600 mb-2">
                <Coffee />
                <span className="font-medium">Absent - {currentAbsence.reason}</span>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                Depuis {new Date(currentAbsence.start_time).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
              </p>
              <Button onClick={handleEndAbsence} className="bg-green-500 hover:bg-green-600">
                <Play className="mr-2" size={18} /> Je suis de retour
              </Button>
            </>
          )}

          {isClockedOut && (
            <>
              <div className="flex items-center justify-center gap-2 text-gray-600 mb-2">
                <CheckCircle />
                <span className="font-medium">Journée terminée</span>
              </div>
              <div className="text-4xl font-bold text-gray-900 mb-2">
                {status.entry.total_hours?.toFixed(2)}h
              </div>
              <p className="text-sm text-gray-500">travaillées aujourd'hui</p>
            </>
          )}

          {!isClockedIn && !isClockedOut && (
            <>
              <div className="flex items-center justify-center gap-2 text-orange-600 mb-4">
                <AlertCircle />
                <span className="font-medium">Pas encore pointé</span>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="grid grid-cols-2 gap-4">
        {!isClockedIn && !isClockedOut && (
          <Button
            onClick={handleClockIn}
            disabled={clockingIn || !deviceFingerprint}
            className="col-span-2 h-16 text-lg bg-green-500 hover:bg-green-600"
          >
            {clockingIn ? (
              <RefreshCw className="mr-2 animate-spin" />
            ) : (
              <LogIn className="mr-2" size={24} />
            )}
            Pointer mon entrée
          </Button>
        )}

        {isClockedIn && !currentAbsence && (
          <>
            <Button
              onClick={() => setShowAbsence(true)}
              variant="outline"
              className="h-14 border-yellow-500 text-yellow-700 hover:bg-yellow-50"
            >
              <Coffee className="mr-2" size={20} />
              M'absenter
            </Button>
            <Button
              onClick={() => setShowReport(true)}
              variant="outline"
              className={`h-14 ${status?.has_daily_report ? 'border-green-500 text-green-700' : 'border-blue-500 text-blue-700'}`}
            >
              <FileText className="mr-2" size={20} />
              {status?.has_daily_report ? 'Modifier CR' : 'Compte-rendu'}
            </Button>
            <Button
              onClick={handleClockOut}
              disabled={clockingOut}
              className="col-span-2 h-14 bg-red-500 hover:bg-red-600"
            >
              {clockingOut ? (
                <RefreshCw className="mr-2 animate-spin" />
              ) : (
                <LogOut className="mr-2" size={20} />
              )}
              Pointer ma sortie
            </Button>
          </>
        )}
      </div>

      {/* Verification Status */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-gray-500">Vérifications</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center gap-2 text-sm">
              <MapPin size={16} className={location ? 'text-green-500' : 'text-gray-400'} />
              Géolocalisation
            </div>
            <span className={`text-xs px-2 py-1 rounded-full ${location ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
              {location ? 'OK' : geoLoading ? 'Chargement...' : 'Non disponible'}
            </span>
          </div>
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center gap-2 text-sm">
              <Fingerprint size={16} className={deviceFingerprint ? 'text-green-500' : 'text-gray-400'} />
              Appareil vérifié
            </div>
            <span className={`text-xs px-2 py-1 rounded-full ${deviceFingerprint ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
              {deviceFingerprint ? 'OK' : 'En cours...'}
            </span>
          </div>
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center gap-2 text-sm">
              <Camera size={16} className="text-gray-400" />
              Selfie requis
            </div>
            <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700">
              Au pointage
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Modals */}
      <AnimatePresence>
        {showSelfie && (
          <SelfieModal
            onCapture={handleSelfieCapture}
            onClose={() => { setShowSelfie(false); setPendingAction(null); }}
          />
        )}
        {showReport && (
          <DailyReportModal
            userId={userId}
            date={today}
            onSubmit={fetchStatus}
            onClose={() => setShowReport(false)}
          />
        )}
        {showAbsence && (
          <AbsenceModal
            userId={userId}
            onStart={fetchStatus}
            onClose={() => setShowAbsence(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
